/**
 * Web twin of native TaskNoteModal for run-step Yes/No evidence.
 *
 * Same chrome: title + char counter, divider, borderless growing field,
 * photo-row + mic tile, Cancel + marine Confirm pinned above the keyboard.
 * Dictation records in the browser and transcribes via the same Gemini
 * verbatim path as native offline notes — works on Android and iOS.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { cameraOutline } from 'ionicons/icons';
import { normalizeProofPhoto, proofBlobToBase64 } from '../services/proofPhoto';
import { uploadRunProofFromWeb } from '../services/runApi';
import { stepNeedsNote, stepNeedsPhoto } from '../runEvidence';
import { useVisualViewport } from '../hooks/useVisualViewport';
import { DICTATION_UNAVAILABLE, useWebDictation } from '../hooks/useWebDictation';
import IonIcon from '../components/IonIcon';
import DictationTile from '../components/DictationTile';
import VoiceLevelBars from '../components/VoiceLevelBars';
import type { WebRunStep } from '../runTypes';

interface Props {
  shortId: string;
  step: WebRunStep;
  outcome: 'yes' | 'no';
  sending: boolean;
  sendError: string | null;
  onConfirm: (input: { details?: string; proofUrls?: string[] }) => void;
  /** Null when the failed action is no longer queued — nothing left to retry. */
  onRetry: (() => void) | null;
  onClose: () => void;
}

const NOTE_MAX = 500;
const MAX_PROOF_PHOTOS = 3;
const NOTE_MIN_HEIGHT = 80;
const NOTE_MAX_HEIGHT = 220;
const THUMB_SIZE = 64;

interface ProofItem {
  id: string;
  /** Already downscaled to JPEG — never re-encode this on the way out. */
  blob: Blob;
  contentType: string;
  previewUrl: string;
}

const DICTATION_HINTS: Record<string, string> = {
  [DICTATION_UNAVAILABLE.NO_PERMISSION]: 'Microphone access is off, so dictation is unavailable.',
  [DICTATION_UNAVAILABLE.FAILED]: 'Dictation could not start. You can still type your note.',
};

function autosizeTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  const next = Math.min(Math.max(el.scrollHeight, NOTE_MIN_HEIGHT), NOTE_MAX_HEIGHT);
  el.style.height = `${next}px`;
}

export default function RunEvidenceModal({
  shortId,
  step,
  outcome,
  sending,
  sendError,
  onConfirm,
  onRetry,
  onClose,
}: Props) {
  const [note, setNote] = useState('');
  const [proofs, setProofs] = useState<ProofItem[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const { height: vvHeight, offsetTop: vvOffsetTop, keyboardOpen, remeasure } = useVisualViewport();

  const requireNote = stepNeedsNote(step, outcome);
  const requirePhoto = stepNeedsPhoto(step, outcome);
  const yes = outcome === 'yes';
  const title = yes ? 'Yes, all good' : 'No — report issue';
  const placeholder = yes ? 'Add a comment…' : 'What is wrong?';
  const confirmLabel = yes ? 'Confirm yes' : 'Report issue';

  const dictation = useWebDictation({
    shortId,
    value: note,
    onChangeText: setNote,
    maxChars: NOTE_MAX,
  });

  useEffect(() => {
    return () => {
      proofs.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    };
    // Only revoke on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    autosizeTextarea(noteRef.current);
  }, [note]);

  const removeProof = useCallback((id: string) => {
    setProofs((prev) => {
      const next = prev.filter((p) => p.id !== id);
      const removed = prev.find((p) => p.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  }, []);

  const handlePhoto = useCallback(async (file: File | null) => {
    // The camera just handed the page back; its size is measured from before.
    remeasure();
    if (!file || proofs.length >= MAX_PROOF_PHOTOS || dictation.isRecording) return;
    setPreviewLoading(true);
    setLocalError(null);
    try {
      const { blob, contentType } = await normalizeProofPhoto(file);
      const previewUrl = URL.createObjectURL(blob);
      setProofs((prev) => {
        if (prev.length >= MAX_PROOF_PHOTOS) {
          URL.revokeObjectURL(previewUrl);
          return prev;
        }
        return [
          ...prev,
          {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            blob,
            contentType,
            previewUrl,
          },
        ];
      });
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Could not read this photo.');
    } finally {
      setPreviewLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [dictation.isRecording, proofs.length, remeasure]);

  const noteLiveRef = useRef(note);
  noteLiveRef.current = note;

  const submit = useCallback(async () => {
    if (dictation.isRecording) {
      const after = await dictation.stop();
      if (after != null) noteLiveRef.current = after;
    }
    const details = noteLiveRef.current.trim();
    const noteMissing = requireNote && !details;
    const photoMissing = requirePhoto && proofs.length === 0;
    if (noteMissing || photoMissing) return;

    setLocalError(null);
    let proofUrls: string[] | undefined;
    if (proofs.length > 0) {
      setUploading(true);
      try {
        proofUrls = [];
        for (const proof of proofs) {
          const base64 = await proofBlobToBase64(proof.blob);
          const uploaded = await uploadRunProofFromWeb({
            shortId,
            stepId: step.stepId,
            contentType: proof.contentType,
            base64,
          });
          if (!uploaded.ok || !uploaded.readUrl) {
            setLocalError(uploaded.error || 'Photo upload failed.');
            return;
          }
          proofUrls.push(uploaded.readUrl);
        }
      } catch {
        setLocalError('Photo upload failed. Check your connection and try again.');
        return;
      } finally {
        setUploading(false);
      }
    }

    onConfirm({
      details: details || undefined,
      proofUrls,
    });
  }, [dictation, note, onConfirm, proofs, requireNote, requirePhoto, shortId, step.stepId]);

  const handleClose = () => {
    if (dictation.isRecording) dictation.cancel();
    onClose();
  };

  const busy = sending || uploading || dictation.converting;
  const error = localError || sendError;
  const noteAtLimit = note.length >= NOTE_MAX;
  const noteMissing = requireNote && !note.trim();
  const photoMissing = requirePhoto && proofs.length === 0;
  const confirmDisabled = busy || noteMissing || photoMissing;
  const canAddMore = proofs.length < MAX_PROOF_PHOTOS;
  const addLabel = proofs.length === 0 ? 'Add proof photo' : 'Add another photo';
  const dictationHint = dictation.unavailableReason
    ? DICTATION_HINTS[dictation.unavailableReason]
    : null;
  const showMic = dictation.supported && !dictation.converting;

  return (
    <div
      className="fixed inset-x-0 z-[70] mx-auto flex w-full max-w-md flex-col bg-white text-x9-ink"
      style={{
        height: vvHeight || '100dvh',
        top: vvOffsetTop,
      }}
    >
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pt-[max(env(safe-area-inset-top),20px)]">
        <div className="mb-1 flex items-center justify-between gap-2">
          <h1 className="min-w-0 flex-1 truncate text-lg font-bold text-x9-ink">{title}</h1>
          <span
            className={`min-w-[3.5rem] shrink-0 text-right text-xs tabular-nums ${
              noteAtLimit ? 'font-bold text-x9-error' : 'text-x9-marineSoft'
            }`}
          >
            {note.length}/{NOTE_MAX}
          </span>
        </div>
        <div className="mb-3 border-t border-x9-marine/10" />

        <textarea
          ref={noteRef}
          id="run-evidence-note"
          value={note}
          maxLength={NOTE_MAX}
          onChange={(e) => setNote(e.target.value)}
          placeholder={placeholder}
          readOnly={dictation.isRecording}
          autoFocus
          rows={3}
          className="mb-4 w-full resize-none border-0 bg-transparent p-0 text-base text-x9-ink outline-none placeholder:text-x9-marineSoft/70 focus:ring-0"
          style={{ minHeight: NOTE_MIN_HEIGHT, maxHeight: NOTE_MAX_HEIGHT }}
        />

        {dictation.mode === 'live' && dictation.isRecording ? (
          <p className="mb-4 text-xs text-x9-marineSoft">Listening…</p>
        ) : dictation.isRecording || dictation.converting ? (
          <div className="mb-4 flex items-center gap-2 text-xs text-x9-marineSoft">
            <VoiceLevelBars level={dictation.meteringLevel} active={dictation.isRecording} />
            <span>{dictation.converting ? 'Converting to text…' : 'Recording…'}</span>
          </div>
        ) : null}

        {dictationHint && !dictation.isRecording ? (
          <p className="mb-4 text-xs text-x9-marineSoft">{dictationHint}</p>
        ) : null}

        <div className="mb-4 flex flex-wrap items-start gap-2">
          {proofs.map((proof) => (
            <div
              key={proof.id}
              className="relative"
              style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
            >
              <img
                src={proof.previewUrl}
                alt=""
                className="size-full rounded-[5px] bg-x9-paper object-cover"
              />
              <button
                type="button"
                onClick={() => removeProof(proof.id)}
                disabled={busy}
                aria-label="Remove photo"
                className="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full bg-white text-x9-error"
              >
                <span className="text-base leading-none" aria-hidden="true">×</span>
              </button>
            </div>
          ))}
          {previewLoading ? (
            <div
              className="flex items-center justify-center rounded-[5px] border-2 border-dashed border-x9-mute bg-x9-paper"
              style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
              aria-label="Preparing photo"
              role="img"
            >
              <span className="size-5 animate-spin rounded-full border-2 border-x9-marine/40 border-t-transparent" />
            </div>
          ) : null}
          {canAddMore ? (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={previewLoading || busy || dictation.isRecording}
              aria-label={addLabel}
              className="flex items-center justify-center rounded-[5px] border-2 border-dashed border-x9-mute bg-x9-paper text-x9-marineSoft disabled:opacity-45"
              style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
            >
              <IonIcon icon={cameraOutline} size={28} />
            </button>
          ) : null}
          {showMic ? (
            <DictationTile
              isRecording={dictation.isRecording}
              remainingSeconds={dictation.remainingSeconds}
              disabled={busy && !dictation.isRecording}
              {...dictation.pressHandlers}
            />
          ) : null}
        </div>
        {/*
          Rendered but invisible rather than display:none — Android returns
          from the camera unreliably when the input it launched is not in the
          layout at all.
        */}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          tabIndex={-1}
          aria-hidden="true"
          className="pointer-events-none absolute size-px overflow-hidden opacity-0"
          onChange={(e) => void handlePhoto(e.target.files?.[0] || null)}
        />

        {error ? (
          <div className="mb-3 rounded-[10px] border border-x9-error/30 bg-x9-error/5 p-3 text-sm text-x9-error">
            <p>{error}</p>
            {sendError && onRetry ? (
              <button type="button" className="mt-2 font-medium text-x9-marine" onClick={onRetry}>
                Retry send
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <footer
        className="shrink-0 border-t border-x9-marine/10 bg-white px-5 pt-2.5"
        style={{
          paddingBottom: keyboardOpen
            ? 10
            : 'max(env(safe-area-inset-bottom), 10px)',
        }}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="min-w-[60px] px-3 py-2 text-center text-base font-medium text-x9-marineSoft disabled:opacity-45"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={confirmDisabled}
            aria-busy={busy}
            className="btn-paper-primary min-h-[48px] flex-1 !rounded-[10px]"
          >
            {busy ? 'Sending…' : confirmLabel}
          </button>
        </div>
      </footer>
    </div>
  );
}
